package com.example.event_management_system.util

import com.example.event_management_system.data.models.Event
import kotlin.js.Date
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.w3c.dom.HTMLIFrameElement

actual fun printEventConfirmation(event: Event) {
    val html = buildEventConfirmationHtml(event)
    val iframe = document.createElement("iframe") as HTMLIFrameElement
    iframe.setAttribute(
        "style",
        "position:fixed;right:0;bottom:0;width:0;height:0;border:0;"
    )
    iframe.setAttribute("aria-hidden", "true")
    iframe.setAttribute("srcdoc", html)
    iframe.onload = {
        window.setTimeout({
            iframe.contentWindow?.focus()
            iframe.contentWindow?.print()
            window.setTimeout({ iframe.remove() }, 1000)
        }, 50)
    }
    document.body?.appendChild(iframe)
}

private fun String.escapeHtml(): String = this
    .replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&#39;")

private fun formatTimestamp(timestamp: Long): String {
    return try {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val dt = instant.toLocalDateTime(TimeZone.UTC)
        val month = dt.month.name.lowercase().replaceFirstChar { it.uppercase() }
        "$month ${dt.dayOfMonth}, ${dt.year}"
    } catch (e: Throwable) {
        "—"
    }
}

private fun formatNow(): String {
    return try {
        val instant = Instant.fromEpochMilliseconds(Date.now().toLong())
        val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val month = dt.month.name.lowercase().replaceFirstChar { it.uppercase() }
        "$month ${dt.dayOfMonth}, ${dt.year}"
    } catch (e: Throwable) {
        ""
    }
}

private fun row(label: String, value: String): String =
    """<div class="row"><span class="label">${label.escapeHtml()}</span><span class="value">${value.escapeHtml()}</span></div>"""

private fun buildEventConfirmationHtml(event: Event): String {
    val date = formatTimestamp(event.eventDate)
    val timeRange = buildString {
        if (event.eventTime.isNotBlank()) {
            append(event.eventTime)
            if (!event.eventEndTime.isNullOrBlank()) append(" – ${event.eventEndTime}")
        }
    }
    val statusLabel = event.status.name.lowercase().replaceFirstChar { it.uppercase() }
    val today = formatNow()

    val customerRows = buildString {
        append(row("Host / Name", event.customerName.ifBlank { "—" }))
        append(row("Email", event.customerEmail.ifBlank { "—" }))
        append(row("Phone", event.customerPhone.ifBlank { "—" }))
        if (!event.catererContact.isNullOrBlank())
            append(row("Caterer Contact", event.catererContact))
    }

    val eventRows = buildString {
        append(row("Date", date))
        if (timeRange.isNotBlank()) append(row("Time", timeRange))
        append(row("Location", event.locationName.ifBlank { "—" }))
        append(row("Guest Count", event.guestCount.toString()))
        if (!event.eventPurpose.isNullOrBlank())
            append(row("Purpose", event.eventPurpose))
    }

    val hasFinancial = !event.cost.isNullOrBlank() || !event.deposit.isNullOrBlank()
    val financialSection = if (hasFinancial) {
        buildString {
            append("""<div class="section"><div class="section-title">Financial</div>""")
            if (!event.cost.isNullOrBlank()) append(row("Total Cost", event.cost))
            if (!event.deposit.isNullOrBlank()) append(row("Deposit", event.deposit))
            append("</div>")
        }
    } else ""

    val notesSection = if (!event.notes.isNullOrBlank()) {
        """<div class="section">
            <div class="section-title">Notes</div>
            <div class="notes-box">${event.notes!!.escapeHtml()}</div>
        </div>"""
    } else ""

    return """<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Event Confirmation – ${event.customerName.escapeHtml()}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #0F172A; background: #fff; padding: 48px; font-size: 14px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 3px solid #1A56DB; margin-bottom: 28px; }
    .brand { font-size: 22px; font-weight: 700; color: #1A56DB; }
    .brand-sub { font-size: 12px; color: #64748B; margin-top: 3px; }
    .doc-info { text-align: right; }
    .doc-title { font-size: 18px; font-weight: 600; }
    .doc-date { font-size: 12px; color: #64748B; margin-top: 3px; }
    .status-row { margin-bottom: 24px; }
    .badge { display: inline-block; padding: 4px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; }
    .badge-PENDING  { background: #FFF7ED; color: #D97706; border: 1px solid #D97706; }
    .badge-CONFIRMED{ background: #F0FDF4; color: #16A34A; border: 1px solid #16A34A; }
    .badge-COMPLETED{ background: #EFF6FF; color: #2563EB; border: 1px solid #2563EB; }
    .badge-CANCELLED{ background: #FFF1F2; color: #DC2626; border: 1px solid #DC2626; }
    .section { margin-bottom: 24px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .09em; color: #64748B; border-bottom: 1px solid #E2E8F0; padding-bottom: 6px; margin-bottom: 10px; }
    .row { display: grid; grid-template-columns: 150px 1fr; padding: 6px 0; border-bottom: 1px solid #F1F5F9; }
    .label { font-size: 13px; color: #64748B; font-weight: 500; }
    .value { font-size: 13px; color: #0F172A; }
    .notes-box { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 12px 14px; font-size: 13px; line-height: 1.6; white-space: pre-wrap; }
    .footer { margin-top: 48px; padding-top: 14px; border-top: 1px solid #E2E8F0; font-size: 11px; color: #94A3B8; text-align: center; }
    @media print {
      body { padding: 15mm 12mm; }
      @page { margin: 0; size: auto; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">${event.locationName.escapeHtml()}</div>
      <div class="brand-sub">Event Confirmation Document</div>
    </div>
    <div class="doc-info">
      <div class="doc-title">Event Confirmation</div>
      <div class="doc-date">Generated: $today</div>
    </div>
  </div>

  <div class="status-row">
    <span class="badge badge-${event.status.name}">$statusLabel</span>
  </div>

  <div class="section">
    <div class="section-title">Customer Information</div>
    $customerRows
  </div>

  <div class="section">
    <div class="section-title">Event Details</div>
    $eventRows
  </div>

  $financialSection

  $notesSection
</body>
</html>"""
}
